import { getUserCoinPackages } from "../../app/helpers/coinPackages";
import { getCoinName, replaceCoinWord } from "../../app/helpers/helper";
import CoinIcon from "../../assets/icons/CoinIcon";
import LeftArrowIcon from "../../assets/icons/LeftArrowIcon";
import RightArrowIcon from "../../assets/icons/RightArrowIcon";
import Bottom from "../../components/Layouts/Bottom";
import BalanceCard from "../../components/common/BalanceCard";
import CustomModal from "../../components/common/CustomModal/CustomModal";
import DynamicCoinPackIcon from "../../components/common/DynamicCoinPackIcon";
import SeriesCoinModalContent from "../../components/common/SeriesCoinModalContent";
import { TOAST_TYPES } from "../../app/constants";
import { useConfig } from "../../app/context/Config/configContext";
import { useNotification } from "../../app/context/Notification";
import EarnCoinsCheckinCard from "./components/EarnCoinsCheckinCard";
import EarnCoinsTaskCard from "./components/EarnCoinsTaskCard";
import gluedin from "gluedin-shorts-js";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

const userModule = new gluedin.GluedInUserModule();

const APP_STORE_URL =
  "https://apps.apple.com/in/app/gluedin-video-community/id1622464243";
const GOOGLE_PLAY_URL =
  "https://play.google.com/store/apps/details?id=com.gluedin.app";

const EarnCoins = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { appConfig } = useConfig();
  const { showNotification } = useNotification();
  const defaultLanguage = localStorage.getItem("defaultLanguage");
  const userData = JSON.parse(localStorage.getItem("userData") || "{}");
  const [coinBalance, setCoinBalance] = useState(
    userData?.coinBalance ?? userData?.coins ?? 0,
  );
  const [checkInSteps, setCheckInSteps] = useState<
    { day: number; coins: number; status: string }[]
  >([]);
  const [checkInEligible, setCheckInEligible] = useState(false);
  const [checkInLoading, setCheckInLoading] = useState(false);
  const [checkInUpdating, setCheckInUpdating] = useState(false);
  const [checkInClaimed, setCheckInClaimed] = useState(false);
  const [checkInAnimateSuccess, setCheckInAnimateSuccess] = useState(false);
  const [showCoinPackModal, setShowCoinPackModal] = useState(false);
  const [showCoinModal, setShowCoinModal] = useState(false);
  const [coinPackages, setCoinPackages] = useState<any[]>([]);
  const [coinPackagesLoading, setCoinPackagesLoading] = useState(false);
  const successAnimationTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );
  const isLoggedIn = Boolean(userData?.userId);

  const triggerCheckInSuccessAnimation = () => {
    setCheckInAnimateSuccess(true);
    if (successAnimationTimerRef.current) {
      clearTimeout(successAnimationTimerRef.current);
    }
    successAnimationTimerRef.current = setTimeout(() => {
      setCheckInAnimateSuccess(false);
      successAnimationTimerRef.current = null;
    }, 1200);
  };

  const checkInConfig =
    appConfig?.appEconomy?.coinSettings?.earnFromCheckIns ??
    appConfig?.appEconomy?.coinSettings?.earnFromCheckin ??
    appConfig?.appEconomy?.coinSettings?.earnFromCheckIn ??
    null;
  const isCheckInEnabled = checkInConfig?.enabled === true;

  const resolveLabel = (key: string, fallback: string) => {
    const value = t(key);
    return value === key ? fallback : value;
  };
  const balanceLabel = resolveLabel("label-balance", "Balance");
  const baseCoinsLabel = resolveLabel("label-coins", "Coins");
  const coinName = getCoinName(appConfig, baseCoinsLabel);
  const withCoinName = (value: string) => replaceCoinWord(value, coinName);
  const coinsLabel = withCoinName(baseCoinsLabel);
  const availableCoinsLabel = withCoinName(
    resolveLabel("label-available-coins", "Available Coins"),
  );
  const earnCoinsLabel = withCoinName(
    resolveLabel("label-earn-coins", "Earn Coins"),
  );
  const earnCoinsTitleLabel = withCoinName(
    resolveLabel("label-earn-coins-title", "Earn Coins"),
  );
  const addLabel = resolveLabel("label-add", "Add");
  const coinPackIconUrl = appConfig?.appEconomy?.coinSettings?.coinUrl;
  const loadingLabel = resolveLabel("label-loading", "Loading");
  const engagementConfig =
    appConfig?.appEconomy?.coinSettings?.earnFromEngagement;
  const engagementAttributes = engagementConfig?.attributes || {};

  const parseEligibility = (value: any) => {
    if (typeof value === "boolean") return value;
    if (typeof value === "number") return value === 1;
    if (typeof value === "string") {
      const normalized = value.trim().toLowerCase();
      if (["true", "1", "yes", "y", "eligible"].includes(normalized)) {
        return true;
      }
      if (["false", "0", "no", "n", "ineligible"].includes(normalized)) {
        return false;
      }
    }
    return null;
  };

  const isSameDay = (timestamp: number) => {
    if (!Number.isFinite(timestamp)) return false;
    const date = new Date(timestamp);
    const today = new Date();
    return (
      date.getFullYear() === today.getFullYear() &&
      date.getMonth() === today.getMonth() &&
      date.getDate() === today.getDate()
    );
  };

  const getDayLabel = (count: number) =>
    resolveLabel(
      count === 1 ? "label-day" : "label-days",
      count === 1 ? "Day" : "Days",
    );

  const computeCoinForDay = (index: number) => {
    const base = Number(checkInConfig?.coin ?? 0);
    const growthValue = Number(checkInConfig?.growthValue ?? 0);
    const growthType = String(checkInConfig?.growthType ?? "").toUpperCase();
    let reward = base;
    if (growthType === "MULTIPLIER") {
      const multiplier =
        Number.isFinite(growthValue) && growthValue !== 0 ? growthValue : 1;
      reward = base * Math.pow(multiplier, index);
    } else if (growthType === "ADDITION" || growthType === "INCREMENTAL") {
      reward = base + growthValue * index;
    }
    const maxCap = Number(checkInConfig?.maxCap);
    if (Number.isFinite(maxCap) && maxCap > 0) {
      reward = Math.min(reward, maxCap);
    }
    return Math.max(0, Math.round(reward));
  };

  const buildCheckInSteps = (claims: number[], showActive: boolean) => {
    const defaultSteps = 5;
    const totalSteps = Math.max(defaultSteps, claims.length + 1);
    const activeIndex = showActive
      ? Math.min(claims.length, totalSteps - 1)
      : -1;

    const steps = Array.from({ length: totalSteps }).map((_, index) => {
      const claimedValue = Number(claims[index]);
      const coins = Number.isFinite(claimedValue)
        ? claimedValue
        : computeCoinForDay(index);
      let status = "upcoming";
      if (index < claims.length) {
        status = "done";
      } else if (index === activeIndex) {
        status = "active";
      }
      return { day: index + 1, coins, status };
    });

    return { steps, activeIndex, totalSteps };
  };

  const activeIndex = useMemo(
    () => checkInSteps.findIndex((step) => step.status === "active"),
    [checkInSteps],
  );
  const claimedCount = useMemo(
    () => checkInSteps.filter((step) => step.status === "done").length,
    [checkInSteps],
  );
  const progressPercent = useMemo(() => {
    if (!checkInSteps.length) return 0;
    const lastDone = checkInSteps.filter(
      (step) => step.status === "done",
    ).length;
    const progressIndex =
      activeIndex >= 0 ? activeIndex : Math.max(lastDone - 1, 0);
    return checkInSteps.length > 1
      ? (progressIndex / (checkInSteps.length - 1)) * 100
      : 0;
  }, [activeIndex, checkInSteps]);

  const tasks = useMemo(() => {
    if (engagementConfig?.enabled !== true) return [];
    const taskMetaMap: Record<
      string,
      { titleKey: string; titleFallback: string; iconType: string }
    > = {
      likeAsViewer: {
        titleKey: "label-like-as-viewer",
        titleFallback: "Like as a Viewer",
        iconType: "LIKE",
      },
      commentAsViewer: {
        titleKey: "label-comment-as-viewer",
        titleFallback: "Comment as a Viewer",
        iconType: "COMMENT",
      },
      shareAsViewer: {
        titleKey: "label-share",
        titleFallback: "Share",
        iconType: "SHARE",
      },
      followAsViewer: {
        titleKey: "label-follow-as-viewer",
        titleFallback: "Follow as a Viewer",
        iconType: "FOLLOW",
      },
      contentCreation: {
        titleKey: "label-create-content",
        titleFallback: "Create Content",
        iconType: "CREATE_CONTENT",
      },
      joinAChallenge: {
        titleKey: "label-join-challenge",
        titleFallback: "Join a Challenge",
        iconType: "JOIN_A_CHALLENGE",
      },
    };

    const orderedKeys = [
      "likeAsViewer",
      "commentAsViewer",
      "shareAsViewer",
      "followAsViewer",
      "contentCreation",
      "joinAChallenge",
    ];

    const attributeSource =
      engagementAttributes && typeof engagementAttributes === "object"
        ? engagementAttributes
        : {};
    const rawKeys = Object.keys(attributeSource);
    const keys = [
      ...orderedKeys.filter((key) => rawKeys.includes(key)),
      ...rawKeys.filter((key) => !orderedKeys.includes(key)),
    ];

    const formatAttributeKeyToLabel = (key: string) =>
      key
        .replace(/AsViewer/g, " as Viewer")
        .replace(/([A-Z])/g, " $1")
        .replace(/\s+/g, " ")
        .trim()
        .replace(/^./, (value) => value.toUpperCase());

    return keys
      .map((key) => {
        const attr = attributeSource[key];
        if (!attr || typeof attr !== "object") return null;

        const meta = taskMetaMap[key];
        const coinsPerAction = Number((attr as any)?.coinsPerAction);
        const dailyLimit = Number((attr as any)?.dailyLimit);
        const coinsText = Number.isFinite(coinsPerAction)
          ? `+${coinsPerAction} ${coinName}`
          : withCoinName(resolveLabel("label-earn-coins", "Earn Coins"));
        const subtitle =
          Number.isFinite(dailyLimit) && dailyLimit > 0
            ? `${coinsText}`
            : coinsText;

        //     const subtitle =
        //   Number.isFinite(dailyLimit) && dailyLimit > 0
        //     ? `${coinsText} • ${dailyLimit} ${resolveLabel(
        //         "label-daily-limit",
        //         "daily limit",
        //       )}`
        //     : coinsText;

        return {
          id: key,
          title: meta
            ? resolveLabel(meta.titleKey, meta.titleFallback)
            : formatAttributeKeyToLabel(key),
          subtitle,
          iconType: meta?.iconType || key.toUpperCase(),
        };
      })
      .filter(Boolean) as Array<{
      id: string;
      title: string;
      subtitle: string;
      iconType: string;
    }>;
  }, [engagementConfig?.enabled, engagementAttributes, coinName, t]);

  const goBack = () => {
    navigate(-1);
  };

  const handleOpenCoinPackModal = () => {
    setShowCoinPackModal(true);
  };

  const handleCloseCoinPackModal = () => {
    setShowCoinPackModal(false);
  };

  const handleCoinPackClick = () => {
    setShowCoinPackModal(false);
    setShowCoinModal(true);
  };

  useEffect(() => {
    let isActive = true;
    const fetchCoinPackages = async () => {
      setCoinPackagesLoading(true);
      try {
        const packages = await getUserCoinPackages();
        if (isActive) {
          setCoinPackages(packages);
        }
      } catch (error) {
        console.error(error);
        if (isActive) {
          setCoinPackages([]);
        }
      } finally {
        if (isActive) {
          setCoinPackagesLoading(false);
        }
      }
    };
    fetchCoinPackages();
    return () => {
      isActive = false;
    };
  }, []);

  useEffect(() => {
    let isActive = true;
    const fetchBalance = async () => {
      if (!isLoggedIn) return;
      try {
        const response = await userModule.getUserBalance();
        const balance = Number(response?.data?.result?.balance);
        if (isActive && Number.isFinite(balance)) {
          setCoinBalance(balance);
        }
      } catch (error) {
        console.error(error);
      }
    };
    fetchBalance();
    return () => {
      isActive = false;
    };
  }, [userData?.userId]);

  useEffect(() => {
    let isActive = true;
    const fetchCheckIn = async () => {
      if (!isLoggedIn || !isCheckInEnabled) return;
      setCheckInLoading(true);
      try {
        const eligibilityResponse =
          await userModule.getUserCheckinEligibility();
        const eligibleRaw = eligibilityResponse?.data?.data?.eligible;
        const eligibleFromApi = parseEligibility(eligibleRaw);

        const statusResponse = await userModule.getUserDailyCheckinStatus();
        const claims = statusResponse?.data?.result?.claims || [];
        const lastCheckInOn = statusResponse?.data?.result?.lastCheckInOn;
        const lastCheckinTimestamp = Number(lastCheckInOn);
        const hasLastCheckin =
          Number.isFinite(lastCheckinTimestamp) && lastCheckinTimestamp > 0;
        const checkedInToday =
          hasLastCheckin && isSameDay(lastCheckinTimestamp);
        const eligible =
          eligibleFromApi !== null ? eligibleFromApi : !checkedInToday;
        const claimed = checkedInToday;
        const safeClaims = Array.isArray(claims) ? claims : [];
        const showNextActive = Boolean(eligible) || claimed;
        const { steps } = buildCheckInSteps(safeClaims, showNextActive);
        if (isActive) {
          setCheckInEligible(Boolean(eligible));
          setCheckInSteps(steps);
          setCheckInClaimed(claimed);
        }
      } catch (error) {
        console.error(error);
      } finally {
        if (isActive) {
          setCheckInLoading(false);
        }
      }
    };
    fetchCheckIn();
    return () => {
      isActive = false;
    };
  }, [isCheckInEnabled, userData?.userId, isLoggedIn]);

  useEffect(() => {
    setCheckInEligible(false);
    setCheckInClaimed(false);
    setCheckInSteps([]);
    setCheckInAnimateSuccess(false);
  }, [userData?.userId]);

  useEffect(() => {
    return () => {
      if (successAnimationTimerRef.current) {
        clearTimeout(successAnimationTimerRef.current);
      }
    };
  }, []);

  const handleCheckinClaim = async () => {
    if (!checkInEligible || checkInUpdating) return;
    setCheckInUpdating(true);
    try {
      const response = await userModule.getUserCheckinUpdate();
      const success =
        response?.data?.success || response?.data?.result === true;
      if (success) {
        setCheckInClaimed(true);
        triggerCheckInSuccessAnimation();
        showNotification({
          title: resolveLabel("label-checkin-success", "Check-in successful"),
          subTitle: withCoinName(
            resolveLabel(
              "label-checkin-success-detail",
              "Coins added to your balance.",
            ),
          ),
          type: TOAST_TYPES.SUCCESS,
        });
      }
      const balanceResponse = await userModule.getUserBalance();
      const balance = Number(balanceResponse?.data?.result?.balance);
      if (Number.isFinite(balance)) {
        setCoinBalance(balance);
      }
      const statusResponse = await userModule.getUserDailyCheckinStatus();
      const claims = statusResponse?.data?.result?.claims || [];
      const lastCheckInOn = statusResponse?.data?.result?.lastCheckInOn;
      const lastCheckinTimestamp = Number(lastCheckInOn);
      const hasLastCheckin =
        Number.isFinite(lastCheckinTimestamp) && lastCheckinTimestamp > 0;
      const claimed = hasLastCheckin && isSameDay(lastCheckinTimestamp);
      const safeClaims = Array.isArray(claims) ? claims : [];
      const { steps } = buildCheckInSteps(safeClaims, true);
      setCheckInSteps(steps);
      setCheckInEligible(false);
      setCheckInClaimed(claimed);
    } catch (error) {
      console.error(error);
      showNotification({
        title: resolveLabel("label-checkin-error", "Check-in failed"),
        subTitle: resolveLabel(
          "label-checkin-error-detail",
          "Please try again later.",
        ),
        type: TOAST_TYPES.ERROR,
      });
    } finally {
      setCheckInUpdating(false);
    }
  };

  return (
    <>
      <div className="full-sec"><div className="right-sec profile-content-list earn-coins-page">
          <div className="full-box earn-coins-full-box">
            <div className="back-header earn-coins-header">
              <ul className="list list-inline">
                <li className="back-btn">
                  <button className="back-btn-a" type="button" onClick={goBack}>
                    {defaultLanguage === "en" ? (
                      <LeftArrowIcon />
                    ) : (
                      <RightArrowIcon />
                    )}
                    {t("back-btn")}
                  </button>
                </li>
                <li className="common-heading">
                  {earnCoinsTitleLabel}
                </li>
              </ul>
            </div>
            <div className="earn-coins-content">
              <BalanceCard
                className="earn-coins-card earn-coins-balance"
                textClassName="earn-coins-balance__text"
                addButtonClassName="earn-coins-balance__add"
                label={availableCoinsLabel}
                balance={coinBalance}
                addLabel={addLabel}
                onAdd={handleOpenCoinPackModal}
              />

              {isCheckInEnabled && isLoggedIn && (
                <EarnCoinsCheckinCard
                  isLoading={checkInLoading}
                  claimedCount={claimedCount}
                  getDayLabel={getDayLabel}
                  checkInClaimed={checkInClaimed}
                  checkInEligible={checkInEligible}
                  checkInUpdating={checkInUpdating}
                  checkInSteps={checkInSteps}
                  activeIndex={activeIndex}
                  progressPercent={progressPercent}
                  animateSuccess={checkInAnimateSuccess}
                  onClaim={handleCheckinClaim}
                  resolveLabel={resolveLabel}
                  t={t}
                />
              )}

              {tasks.map((task) => (
                <EarnCoinsTaskCard
                  key={task.id}
                  task={task}
                  actionLabel={earnCoinsLabel}
                  onAction={() =>
                    navigate(
                      task.id === "contentCreation"
                        ? "/create-post"
                        : "/vertical-player",
                    )
                  }
                />
              ))}
            </div>
          </div>
        </div>
      </div>
      {showCoinPackModal && (
        <CustomModal
          isOpen={showCoinPackModal}
          close={handleCloseCoinPackModal}
          classes={"coin-wallet-unlock-modal"}
        >
          <div className="series-unlock series-unlock--open coin-wallet-unlock">
            <div className="earn-coins-unlock__balance">
              <span>{balanceLabel}:</span>
              <CoinIcon className="gi-coin-icon gi-coin-small" aria-hidden />
              <span>{coinBalance}</span>
            </div>
            <div className="series-packs__container">
              <h4>Buy Coins</h4>
              <div className="series-unlock__packs">
                {coinPackagesLoading && (
                  <p className="series-unlock__subtitle">{loadingLabel}...</p>
                )}
                {!coinPackagesLoading &&
                  coinPackages?.map((pack) => (
                    <button
                      key={pack.id}
                      type="button"
                      className="series-unlock__pack"
                      onClick={handleCoinPackClick}
                    >
                      <DynamicCoinPackIcon coinUrl={coinPackIconUrl} />
                      <span className="series-unlock__pack-text">
                        <span className="series-unlock__pack-amount">
                          {pack.coins} {coinsLabel}
                        </span>
                        <span className="series-unlock__pack-price series-unlock__pack-price--blur">
                          {pack.priceLabel ||
                            (Number.isFinite(Number(pack.price))
                              ? `$${Number(pack.price).toFixed(2)}`
                              : "")}
                        </span>
                      </span>
                    </button>
                  ))}
              </div>
            </div>
          </div>
        </CustomModal>
      )}

      {showCoinModal && (
        <CustomModal
          isOpen={showCoinModal}
          close={() => setShowCoinModal(false)}
          classes={"series-coin-modal"}
        >
          <SeriesCoinModalContent
            title={t("label-download-our-app")}
            subtitle={t("label-download-our-app-subtitle")}
            detail={t("label-feature-available-app-detail")}
            appStoreUrl={APP_STORE_URL}
            googlePlayUrl={GOOGLE_PLAY_URL}
          />
        </CustomModal>
      )}
      <Bottom />
    </>
  );
};

export default EarnCoins;
